import React, { createContext, useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ConfigProvider } from 'antd';
import dayjs from 'dayjs';

import { selectAppLanguage } from '../store/selectors/i18n.selectors';

type AntdLocale = NonNullable<React.ComponentProps<typeof ConfigProvider>['locale']>;
type DayjsLocaleDefinition = Exclude<Parameters<typeof dayjs.locale>[0], string | undefined>;
interface AntdLocaleModule {
  readonly default: AntdLocale;
}

const localeLoaders = import.meta.glob<AntdLocaleModule>('/node_modules/antd/es/locale/*_*.js');
const englishLocalePath = '/node_modules/antd/es/locale/en_GB.js';
const dayjsLocaleLoaders = import.meta.glob<DayjsLocaleDefinition>('/node_modules/dayjs/esm/locale/*.js', { import: 'default' });
const englishDayjsLocalePath = '/node_modules/dayjs/esm/locale/en.js';
const DayjsLocaleContext = createContext<string | undefined>(undefined);

export const useDayjsLocale = () => useContext(DayjsLocaleContext);

interface AntdLocaleProviderProps {
  readonly children: React.ReactNode;
}

const loadAntdLocale = async (localeToString: string): Promise<AntdLocale | undefined> => {
  const localePath = `/node_modules/antd/es/locale/${localeToString}.js`;
  const loadEnglishLocale = localeLoaders[englishLocalePath];

  if (!loadEnglishLocale) {
    return undefined;
  }

  try {
    const loadLocale = localeLoaders[localePath] ?? loadEnglishLocale;
    return (await loadLocale()).default;
  } catch {
    return (await loadEnglishLocale()).default;
  }
};

const loadDayjsLocale = async (localeToString: string): Promise<DayjsLocaleDefinition | undefined> => {
  const [language] = localeToString.toLowerCase().split('_');
  const languageLocalePath = `/node_modules/dayjs/esm/locale/${language}.js`;
  const loadEnglishLocale = dayjsLocaleLoaders[englishDayjsLocalePath];
  const loadLocale = dayjsLocaleLoaders[languageLocalePath] ?? loadEnglishLocale;

  if (!loadLocale) {
    return undefined;
  }

  try {
    return await loadLocale();
  } catch {
    if (!loadEnglishLocale || loadLocale === loadEnglishLocale) {
      return undefined;
    }

    return loadEnglishLocale();
  }
};

export const AntdLocaleProvider = ({ children }: AntdLocaleProviderProps) => {
  const language = useSelector(selectAppLanguage);
  const localeToString = language ? `${language.language.toLocaleLowerCase()}_${language.country.toLocaleUpperCase()}` : undefined;

  const [locale, setLocale] = useState<AntdLocale>();
  const [dayjsLocale, setDayjsLocale] = useState<string>();
  const [theme, setTheme] = useState<React.ComponentProps<typeof ConfigProvider>['theme']>();
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!localeToString) {
      return;
    }

    let isCurrentLocale = true;

    void Promise.all([
      import('@vtmn-play/antd').then(({ default: loadedTheme }) => loadedTheme),
      loadAntdLocale(localeToString),
      loadDayjsLocale(localeToString),
    ])
      .then(([loadedTheme, loadedLocale, loadedDayjsLocale]) => {
        if (isCurrentLocale) {
          setTheme(loadedTheme);
          if (loadedDayjsLocale) {
            dayjs.locale(loadedDayjsLocale, undefined, true);
            dayjs.locale(loadedDayjsLocale.name);
            setDayjsLocale(loadedDayjsLocale.name);
          } else {
            dayjs.locale('en');
            setDayjsLocale('en');
          }
          setLocale(loadedLocale);
        }
      })
      .catch(() => {
        if (isCurrentLocale) {
          dayjs.locale('en');
          setDayjsLocale('en');
        }
      })
      .finally(() => {
        if (isCurrentLocale) {
          setIsReady(true);
        }
      });

    return () => {
      isCurrentLocale = false;
    };
  }, [localeToString]);

  return (
    <DayjsLocaleContext.Provider value={dayjsLocale}>
      <ConfigProvider locale={locale} theme={theme}>
        {localeToString && isReady ? children : null}
      </ConfigProvider>
    </DayjsLocaleContext.Provider>
  );
};
